import { getFirebaseAuth } from "@risalabs_frontend_org/auth-wrapper";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { RbacRoleId } from "../../enums/rbacRoles";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import {
  BulkUpdatePayload,
  BulkUpdateResponse,
  FacilityOption,
  InviteMemberPayload,
  InviteMemberResponse,
  MemberManagementError,
  ProviderWithFacilities,
  RbacTokenErrorType,
  RbacTokenResult,
  RemoveMemberResponse,
  StoredTokenData,
  TokenFacilityInfo,
  TokenPermissions,
  TokenResponse,
  TokenRoleInfo,
  TokenUserInfo,
  UpdateMemberRolePayload,
  UpdateMemberRoleResponse,
} from "./memberManagementTypes";

class MemberManagementTokenManager {
  private static instance: MemberManagementTokenManager;

  // Auto-refresh: refresh 5 minutes before expiration
  private static readonly AUTO_REFRESH_BUFFER_MS = 5 * 60 * 1000;

  // SECURITY: Tokens intentionally stored in-memory only.
  // Do NOT persist to localStorage/sessionStorage.
  private token: string | null = null;
  private expirationTime: number | null = null;
  private currentFacilityId: string | null = null;
  private userInfo: TokenUserInfo | null = null;
  private facilityInfo: TokenFacilityInfo | null = null;
  private roleInfo: TokenRoleInfo | null = null;
  private lastErrorType: RbacTokenErrorType | null = null;
  private lastErrorMessage: string | null = null;

  // Request deduplication: prevents multiple simultaneous token fetch requests
  private fetchPromise: Promise<string | null> | null = null;
  private pendingFacilityId: string | null = null;

  // Auto token refresh timer
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor() {}

  public static getInstance(): MemberManagementTokenManager {
    if (!MemberManagementTokenManager.instance) {
      MemberManagementTokenManager.instance =
        new MemberManagementTokenManager();
    }
    return MemberManagementTokenManager.instance;
  }

  /**
   * Get the Firebase ID token from the currently authenticated user
   */
  private async getFirebaseIdToken(): Promise<string | null> {
    const currentUser = getFirebaseAuth().currentUser;
    if (!currentUser) {
      logError(new Error("No authenticated user found"), "getFirebaseIdToken");
      return null;
    }
    try {
      const idToken = await currentUser.getIdToken();
      return idToken;
    } catch (error) {
      logError(error as Error, "Error getting Firebase ID token");
      return null;
    }
  }

  /**
   * Parse error response to determine error type and message
   */
  private parseErrorResponse(
    status: number,
    errorText: string,
  ): { errorType: RbacTokenErrorType; errorMessage: string } {
    const errorMessage = this.extractErrorMessage(errorText);
    const errorType = this.getErrorType(status, errorMessage);
    return { errorType, errorMessage };
  }

  private extractErrorMessage(errorText: string): string {
    try {
      const { detail, message } = JSON.parse(errorText);
      return detail ?? message ?? errorText;
    } catch {
      return errorText || "An error occurred. Please try again.";
    }
  }

  private getErrorType(status: number, message: string): RbacTokenErrorType {
    if (status === 401) return "unauthorized";

    if (status === 403) {
      const lowerMessage = message.toLowerCase();
      const isInactiveAccount =
        lowerMessage.includes("inactive") || lowerMessage.includes("disabled");
      return isInactiveAccount ? "inactive_account" : "forbidden";
    }

    return "unknown";
  }

  /**
   * Check if the current cached token is valid for the given facility
   */
  private isTokenValid(facilityId: string): boolean {
    return !!(
      this.token &&
      this.expirationTime &&
      this.currentFacilityId === facilityId &&
      Date.now() < this.expirationTime
    );
  }

  /**
   * Schedule automatic token refresh before expiration
   */
  private scheduleTokenRefresh(facilityId: string): void {
    // Clear any existing refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.expirationTime) return;

    // Calculate when to refresh (5 minutes before expiration)
    const refreshIn =
      this.expirationTime -
      Date.now() -
      MemberManagementTokenManager.AUTO_REFRESH_BUFFER_MS;

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        logDataToConsole("Auto-refreshing RBAC token before expiration");
        // Clear the current token to force a refresh
        this.token = null;
        this.fetchToken(facilityId).catch((error) => {
          logError(error as Error, "Error during automatic token refresh");
        });
      }, refreshIn);
    }
  }

  /**
   * Exchange Firebase ID token for backend JWT
   * Uses request deduplication to prevent multiple simultaneous API calls
   */
  public async fetchToken(facilityId: string): Promise<string | null> {
    this.lastErrorType = null;
    this.lastErrorMessage = null;

    // Return cached token if still valid for this facility
    if (this.isTokenValid(facilityId)) {
      return this.token;
    }

    // Request deduplication: if a fetch is already in progress for the same facility, reuse it
    if (this.fetchPromise && this.pendingFacilityId === facilityId) {
      return this.fetchPromise;
    }

    // Start a new fetch
    this.pendingFacilityId = facilityId;
    this.fetchPromise = this.performTokenFetch(facilityId);

    try {
      const result = await this.fetchPromise;
      return result;
    } finally {
      this.fetchPromise = null;
      this.pendingFacilityId = null;
    }
  }

  /**
   * Performs the actual token fetch API call
   * @private
   */
  private async performTokenFetch(facilityId: string): Promise<string | null> {
    const firebaseToken = await this.getFirebaseIdToken();
    if (!firebaseToken) {
      console.error("Failed to get Firebase ID token");
      this.lastErrorType = "unauthorized";
      this.lastErrorMessage = "Failed to authenticate. Please log in again.";
      return null;
    }

    try {
      const response = await fetch(API_ENDPOINTS.AUTH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "firebase-token": firebaseToken,
          "X-Facility-ID": facilityId,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token exchange failed:", {
          status: response.status,
          statusText: response.statusText,
          facilityId: facilityId,
          // Note: errorText may contain sensitive info, logged for debugging only
        });
        logError(
          new Error(
            `Token exchange failed: ${response.status} ${response.statusText}`,
          ),
          errorText,
        );

        const { errorType, errorMessage } = this.parseErrorResponse(
          response.status,
          errorText,
        );
        this.lastErrorType = errorType;
        this.lastErrorMessage = errorMessage;

        return null;
      }

      const data: TokenResponse = await response.json();

      this.token = data.access_token;
      this.expirationTime = Date.now() + data.expires_in * 1000;
      this.currentFacilityId = facilityId;
      this.userInfo = data.user;
      this.facilityInfo = data.facility;
      this.roleInfo = data.role;

      // Schedule automatic refresh before token expires
      this.scheduleTokenRefresh(facilityId);

      return this.token;
    } catch (error) {
      logError(error as Error, "Error exchanging token");
      this.lastErrorType = "unknown";
      this.lastErrorMessage = "An error occurred. Please try again.";
      return null;
    }
  }

  /**
   * Get the last error type from token fetch
   */
  public getLastErrorType(): RbacTokenErrorType | null {
    return this.lastErrorType;
  }

  /**
   * Get the last error message from token fetch
   */
  public getLastErrorMessage(): string | null {
    return this.lastErrorMessage;
  }

  /**
   * Clear the cached token and all stored data
   * Also cancels any pending refresh timers
   */
  public clearToken(): void {
    // Cancel any scheduled refresh
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.token = null;
    this.expirationTime = null;
    this.currentFacilityId = null;
    this.userInfo = null;
    this.facilityInfo = null;
    this.roleInfo = null;
    this.lastErrorType = null;
    this.lastErrorMessage = null;
    this.fetchPromise = null;
    this.pendingFacilityId = null;
  }

  /**
   * Get the stored user information
   */
  public getUserInfo(): TokenUserInfo | null {
    return this.userInfo;
  }

  /**
   * Get the stored facility information
   */
  public getFacilityInfo(): TokenFacilityInfo | null {
    return this.facilityInfo;
  }

  /**
   * Get the stored role information
   */
  public getRoleInfo(): TokenRoleInfo | null {
    return this.roleInfo;
  }

  /**
   * Get the user's permissions
   */
  public getPermissions(): TokenPermissions | null {
    return this.roleInfo?.permissions ?? null;
  }

  /**
   * Check if user has a specific permission
   */
  public hasPermission(permission: keyof TokenPermissions): boolean {
    return this.roleInfo?.permissions?.[permission] ?? false;
  }

  /**
   * Get all stored token data
   */
  public getStoredTokenData(): StoredTokenData | null {
    if (!this.token || !this.expirationTime || !this.currentFacilityId) {
      return null;
    }
    if (!this.userInfo || !this.facilityInfo || !this.roleInfo) {
      return null;
    }
    return {
      token: this.token,
      expirationTime: this.expirationTime,
      facilityId: this.currentFacilityId,
      user: this.userInfo,
      facility: this.facilityInfo,
      role: this.roleInfo,
    };
  }

  /**
   * Check if the current user is an admin
   */
  public isAdmin(): boolean {
    return this.userInfo?.IsAdmin ?? false;
  }

  /**
   * Get the current facility ID
   */
  public getCurrentFacilityId(): string | null {
    return this.currentFacilityId;
  }
}

/**
 * Extract a user-friendly error message from API error response
 * Handles both string errors and Pydantic validation error arrays
 */
function extractErrorMessage(
  errorData: Record<string, unknown>,
  fallback: string,
): string {
  // Handle Pydantic validation errors (array of {type, loc, msg, input, ctx})
  if (Array.isArray(errorData.detail)) {
    const messages = errorData.detail
      .map((err: { msg?: string; loc?: (string | number)[] }) => {
        if (typeof err.msg === "string") {
          // Include location context if available
          if (Array.isArray(err.loc) && err.loc.length > 0) {
            const field = err.loc[err.loc.length - 1];
            return `${field}: ${err.msg}`;
          }
          return err.msg;
        }
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join("; ") : fallback;
  }

  // Handle string detail
  if (typeof errorData.detail === "string") {
    return errorData.detail;
  }

  // Handle message field
  if (typeof errorData.message === "string") {
    return errorData.message;
  }

  return fallback;
}

/**
 * Get the backend JWT token for member management operations
 */
async function getBackendToken(facilityId: string): Promise<string> {
  const tokenManager = MemberManagementTokenManager.getInstance();
  const token = await tokenManager.fetchToken(facilityId);
  if (!token) {
    throw new Error("Unauthorized");
  }
  return token;
}

/**
 * Invite a new member to the facility
 *
 * @param facilityId - The facility ID
 * @param payload - The member invite payload (name, email, roleId)
 * @returns Promise with the invite response
 * @throws Error with status 401 (unauthorized), 403 (forbidden), 409 (conflict)
 */
export async function inviteMember(
  facilityId: string,
  payload: InviteMemberPayload,
): Promise<InviteMemberResponse> {
  const token = await getBackendToken(facilityId);

  const response = await fetch(API_ENDPOINTS.MEMBER_INVITE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Remove a member from the facility
 *
 * @param facilityId - The facility ID
 * @param userId - The user ID to remove
 * @returns Promise with the remove response
 * @throws Error with status 401 (unauthorized), 403 (forbidden), 404 (not found)
 */
export async function removeMember(
  facilityId: string,
  userId: string,
): Promise<RemoveMemberResponse> {
  const token = await getBackendToken(facilityId);

  const response = await fetch(`${API_ENDPOINTS.MEMBER_BASE}/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Update a member's role in the facility
 *
 * @param facilityId - The facility ID
 * @param userId - The user ID to update
 * @param payload - The new role payload
 * @returns Promise with the update response
 * @throws Error with status 401 (unauthorized), 403 (forbidden), 404 (not found)
 */
export async function updateMemberRole(
  facilityId: string,
  userId: string,
  payload: UpdateMemberRolePayload,
): Promise<UpdateMemberRoleResponse> {
  const token = await getBackendToken(facilityId);

  const response = await fetch(`${API_ENDPOINTS.MEMBER_BASE}/${userId}/role`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Bulk update member roles across multiple facilities (Super Admin only)
 *
 * @param facilityId - The current facility ID (for token context)
 * @param payload - The bulk update payload with facility-role assignments
 * @returns Promise with the bulk update response
 * @throws Error with status 401 (unauthorized), 403 (forbidden), 422 (validation error)
 */
export async function bulkUpdateMember(
  facilityId: string,
  payload: BulkUpdatePayload,
): Promise<BulkUpdateResponse> {
  const token = await getBackendToken(facilityId);

  const response = await fetch(API_ENDPOINTS.MEMBER_BULK_UPDATE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Clear the cached backend token (call on logout)
 */
export function clearMemberManagementToken(): void {
  MemberManagementTokenManager.getInstance().clearToken();
}

/**
 * Fetch and store the RBAC token for the given facility
 * Call this once on app mount after user is authenticated
 *
 * @param facilityId - The facility ID to fetch token for
 * @returns Promise<RbacTokenResult> - Result with success status and error details if failed
 */
export async function fetchRbacToken(
  facilityId: string,
): Promise<RbacTokenResult> {
  const tokenManager = MemberManagementTokenManager.getInstance();
  const token = await tokenManager.fetchToken(facilityId);

  if (token !== null) {
    return { success: true };
  }

  return {
    success: false,
    errorType: tokenManager.getLastErrorType() ?? "unknown",
    errorMessage:
      tokenManager.getLastErrorMessage() ??
      "An error occurred. Please try again.",
  };
}

/**
 * Get the stored user information from the token response
 */
export function getTokenUserInfo(): TokenUserInfo | null {
  return MemberManagementTokenManager.getInstance().getUserInfo();
}

/**
 * Get the stored facility information from the token response
 */
export function getTokenFacilityInfo(): TokenFacilityInfo | null {
  return MemberManagementTokenManager.getInstance().getFacilityInfo();
}

/**
 * Get the stored role information from the token response
 */
export function getTokenRoleInfo(): TokenRoleInfo | null {
  return MemberManagementTokenManager.getInstance().getRoleInfo();
}

/**
 * Get the user's permissions from the token response
 */
export function getTokenPermissions(): TokenPermissions | null {
  return MemberManagementTokenManager.getInstance().getPermissions();
}

/**
 * Check if user has a specific permission
 */
export function hasTokenPermission(
  permission: keyof TokenPermissions,
): boolean {
  return MemberManagementTokenManager.getInstance().hasPermission(permission);
}

/**
 * Get all stored token data
 */
export function getStoredTokenData(): StoredTokenData | null {
  return MemberManagementTokenManager.getInstance().getStoredTokenData();
}

/**
 * Check if the current user is an admin
 */
export function isCurrentUserAdmin(): boolean {
  const roleInfo = MemberManagementTokenManager.getInstance().getRoleInfo();
  return roleInfo?.id === RbacRoleId.ADMIN_MED_ONC;
}

/**
 * Get the current facility ID
 */
export function getCurrentFacilityId(): string | null {
  return MemberManagementTokenManager.getInstance().getCurrentFacilityId();
}

/**
 * Get the user's internal healthcare facilities from the token response
 */
export function getUserInternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getUserInfo()
      ?.internal_healthcare_facilities ?? []
  );
}

/**
 * Get the user's external healthcare facilities from the token response
 */
export function getUserExternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getUserInfo()
      ?.external_healthcare_facilities ?? []
  );
}

/**
 * Get the facility's internal facilities from the token response
 */
export function getFacilityInternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getFacilityInfo()
      ?.internal_facilities ?? []
  );
}

/**
 * Get the facility's external facilities from the token response
 */
export function getFacilityExternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getFacilityInfo()
      ?.external_facilities ?? []
  );
}

/**
 * Helper function to convert UI role to API role ID
 */
export function mapUiRoleToApiRoleId(
  uiRole: "admin" | "member" | "Admin" | "Member",
): RbacRoleId.ADMIN_MED_ONC | RbacRoleId.STANDARD_MED_ONC {
  const lowerRole = uiRole.toLowerCase();
  return lowerRole === "admin"
    ? RbacRoleId.ADMIN_MED_ONC
    : RbacRoleId.STANDARD_MED_ONC;
}

/**
 * Helper function to convert API role ID to UI role
 */
export function mapApiRoleIdToUiRole(roleId: string): "Admin" | "Member" {
  return roleId === RbacRoleId.ADMIN_MED_ONC ? "Admin" : "Member";
}

/**
 * Get user-friendly error message from member management error
 */
export function getMemberErrorMessage(error: MemberManagementError): string {
  switch (error.status) {
    case 401:
      return "You are not authenticated. Please log in again.";
    case 403:
      return "You do not have permission to perform this action. Admin access required.";
    case 404:
      return "Member not found in this facility.";
    case 409:
      return "This member already exists in the facility or has a pending invitation.";
    case 422:
      return error.message || "Validation error. Please check your input.";
    case 500:
      return "An unexpected server error occurred. Please try again later.";
    default:
      return error.message || "An error occurred. Please try again.";
  }
}

/**
 * Check if the current user is a super admin
 */
export function isCurrentUserSuperAdmin(): boolean {
  const roleInfo = MemberManagementTokenManager.getInstance().getRoleInfo();
  return roleInfo?.id === RbacRoleId.SUPER_ADMIN;
}

// ============================================================================
// Super Admin Helper Functions - Fetch all providers and facilities
// ============================================================================

interface ProviderDocument {
  uid?: string;
  FirstName?: string;
  LastName?: string;
  EmailAddresses?: string[];
  Status?: string;
  internal_healthcare_facilities?: string[];
  external_healthcare_facilities?: string[];
  healthcare_facilities?: string[]; // Legacy field
}

interface FacilityProviderDocument {
  role_id?: string;
  Status?: string;
}

/**
 * Fetch all healthcare facilities with their internal/external classification
 * For Super Admin use
 */
export async function fetchAllFacilities(): Promise<FacilityOption[]> {
  try {
    const facilities =
      await FirestoreService.getAllDocuments<HealthCareFacilityModel>(
        FirestoreCollectionReference.healthcareFacility(),
      );

    return facilities.map((facility) => ({
      id: facility.DocId || "",
      name: facility.Name || "Unknown Facility",
      isExternal: facility.is_external_organization === true,
    }));
  } catch (error) {
    logError(error as Error, "Error fetching all facilities");
    throw error;
  }
}

/**
 * Fetch all providers from the providers collection
 * For Super Admin use - gets providers with their facility assignments
 */
export async function fetchAllProviders(
  facilityMap: Map<string, FacilityOption>,
): Promise<ProviderWithFacilities[]> {
  try {
    const providers = await FirestoreService.getAllDocuments<ProviderDocument>(
      FirestoreCollectionReference.providers(),
    );

    const providersWithFacilities: ProviderWithFacilities[] = [];

    for (const provider of providers) {
      if (!provider.uid || provider.Status === "disabled") continue;

      const internalFacilityIds = provider.internal_healthcare_facilities || [];
      const externalFacilityIds = provider.external_healthcare_facilities || [];

      // Get facility names
      const internalFacilityNames = internalFacilityIds
        .map((id) => facilityMap.get(id)?.name || id)
        .filter(Boolean);
      const externalFacilityNames = externalFacilityIds
        .map((id) => facilityMap.get(id)?.name || id)
        .filter(Boolean);

      // Fetch roles from each facility's providers subcollection
      const facilityRoles: Record<
        string,
        RbacRoleId.ADMIN_MED_ONC | RbacRoleId.STANDARD_MED_ONC
      > = {};

      const allFacilityIds = [...internalFacilityIds, ...externalFacilityIds];
      for (const facilityId of allFacilityIds) {
        try {
          const providerDoc =
            await FirestoreService.getDocument<FacilityProviderDocument>(
              `${FirestoreCollectionReference.healthFacilityProviders(facilityId)}/${provider.uid}`,
            );
          if (providerDoc?.role_id) {
            facilityRoles[facilityId] = providerDoc.role_id as
              | RbacRoleId.ADMIN_MED_ONC
              | RbacRoleId.STANDARD_MED_ONC;
          }
        } catch {
          // Provider might not be in this facility's subcollection
          facilityRoles[facilityId] = RbacRoleId.STANDARD_MED_ONC;
        }
      }

      providersWithFacilities.push({
        id: provider.uid,
        name:
          `${provider.FirstName || ""} ${provider.LastName || ""}`.trim() ||
          "Unknown",
        email: provider.EmailAddresses?.[0] || "",
        status: provider.Status || "active",
        internalFacilityIds,
        externalFacilityIds,
        internalFacilityNames,
        externalFacilityNames,
        facilityRoles,
      });
    }

    return providersWithFacilities;
  } catch (error) {
    logError(error as Error, "Error fetching all providers");
    throw error;
  }
}

/**
 * Fetch all providers and facilities for Super Admin view
 * Returns both the facilities list and providers with their assignments
 */
export async function fetchSuperAdminData(): Promise<{
  facilities: FacilityOption[];
  internalFacilities: FacilityOption[];
  externalFacilities: FacilityOption[];
  providers: ProviderWithFacilities[];
}> {
  try {
    const facilities = await fetchAllFacilities();

    // Create facility map for quick lookup
    const facilityMap = new Map<string, FacilityOption>();
    facilities.forEach((f) => facilityMap.set(f.id, f));

    // Separate internal and external facilities
    const internalFacilities = facilities.filter((f) => !f.isExternal);
    const externalFacilities = facilities.filter((f) => f.isExternal);

    // Fetch all providers with their facility assignments
    const providers = await fetchAllProviders(facilityMap);

    return {
      facilities,
      internalFacilities,
      externalFacilities,
      providers,
    };
  } catch (error) {
    logError(error as Error, "Error fetching super admin data");
    throw error;
  }
}

export type FacilityType = "internal" | "external" | "not_assigned";

/**
 * Determines the facility type for the given organization ID based on the user's token info.
 *
 * @param orgId - The organization/facility ID to check
 * @returns "internal" if orgId is in internal_healthcare_facilities,
 *          "external" if orgId is in external_healthcare_facilities,
 *          "not_assigned" otherwise
 */
export const userLoggedInFacilityType = (orgId: string): FacilityType => {
  const userInfo = getTokenUserInfo();

  if (!userInfo || !orgId) {
    return "not_assigned";
  }

  const internalFacilities = userInfo.internal_healthcare_facilities ?? [];
  const externalFacilities = userInfo.external_healthcare_facilities ?? [];

  if (internalFacilities.includes(orgId)) {
    return "internal";
  }

  if (externalFacilities.includes(orgId)) {
    return "external";
  }

  return "not_assigned";
};

/**
 * Get the current user's ID from the stored token data
 *
 * @returns The user's ID if available, undefined if no token data is stored or user info is not present
 */
export function getCurrentUserId(): string | undefined {
  return MemberManagementTokenManager.getInstance().getUserInfo()?.id;
}

/**
 * Get the current user's email from the stored token data
 *
 * @returns The user's email if available, undefined if no token data is stored or user info is not present
 */
export function getCurrentUserEmail(): string | undefined {
  return MemberManagementTokenManager.getInstance().getUserInfo()
    ?.EmailAddresses?.[0];
}
